import { RefundStatus, RefundType } from "../../models/refundType";
import { firestore } from "../../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";


const REFUND_COLLECTION = "refund-collection"; // Replace with your Firestore collection name

export interface RefundMetrics {
    pending: number;
    approved: number;
    partialApproved: number;
    denied: number;
    pendingYoco: number;
}

export const refundService = {
    /**
     * Fetch a specific refund by id
     * @param id - The refund id
     * @returns A RefundType object
     */
    async fetchRefundById(id: string): Promise<RefundType> {
        const docRef = doc(firestore, REFUND_COLLECTION, id); // Reference to the specific document by ID
        const snapshot = await getDoc(docRef);

        if (!snapshot.exists()) {
            throw new Error(`Refund with ID ${id} not found.`);
        }

        const data = snapshot.data();
        return data as RefundType; // Convert Firestore data to FullProperty instance
    },


    /**
     * Fetch refund counts by restaurant, grouped by status
     * @param restaurantId – the restaurant’s UID
     * @returns counts of refunds in each category
     */
    async fetchNumberRefundsByRestaurant(restaurantId: string): Promise<RefundMetrics> {
        // Build a query for all refunds belonging to this restaurant
        const refundsRef = collection(firestore, "REFUND_COLLECTION");
        const q = query(refundsRef, where("restaurantId", "==", restaurantId));

        const snapshot = await getDocs(q);

        // Initialize counters
        const metrics: RefundMetrics = {
            pending: 0,
            approved: 0,
            partialApproved: 0,
            denied: 0,
            pendingYoco: 0
        };

        snapshot.forEach((doc) => {
            const data = doc.data() as RefundType;
            switch (data.rufundStatus as RefundStatus) {
                case "PENDING_RESTAURANT":
                    metrics.pending += 1;
                    break;
                case "FULL_REFUND_COMPLETE":
                    metrics.approved += 1;
                    break;
                case "PARTIAL_REFUND_COMPLETE":
                    metrics.partialApproved += 1;
                    break;
                case "REFUND_REJECTED":
                    metrics.denied += 1;
                    break;
                case "FULL_REFUND_PROCESSING":
                case "PARTIAL_REFUND_PROCESSING":
                    metrics.pendingYoco += 1;
                    break;
                default:
                    break;
            }
        });

        return metrics;
    },
    /**
 * Fetch all refunds for a given restaurant that are still pending the restaurant’s approval.
 * @param restaurantId – the restaurant’s UID
 * @returns an array of RefundType whose status is PENDING_RESTAURANT
 */
    async fetchPendingRefundsByRestaurant(
        restaurantId: string
    ): Promise<RefundType[]> {
        const refundsRef = collection(firestore, REFUND_COLLECTION);
        const q = query(
            refundsRef,
            where("restaurantId", "==", restaurantId),
            where("rufundStatus", "==", "PENDING_RESTAURANT")
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => doc.data() as RefundType);
    },
};
